if ( ! customElements.get('product-variants') ) {

	class ProductVariants extends HTMLElement {

		constructor() {
			super();
			this.init();
		}

		init() {

			// Inside a product card, resolve the price block and the form within that card.
			// data-id is block.id + section_id + the product loop index, which is not unique
			// across a page: a Featured collection section with several collections renders
			// every tab at once and the index restarts per collection. A document-wide lookup
			// bound tabs 2..N's variant pickers to tab 1's form and price block. On the PDP
			// there is no <quick-buy> ancestor, so this falls back to document unchanged.
			this.scope = this.closest('quick-buy') || document;

			this.price = this.scope.querySelector(`#product-price-${this.dataset.id}`);

			if ( this.price ) {
				this.priceOriginal = this.price.querySelector('[data-js-product-price-original]');
				this.priceCompare = this.price.querySelector('[data-js-product-price-compare]');
				this.priceUnit = this.price.querySelector('[data-js-product-price-unit]');
			}

			this.productForm = this.scope.querySelector(`#product-form-${this.dataset.id}`);
			if ( this.productForm ) {
				this.productQty = this.productForm.querySelector('[data-js-product-quantity]');
				this.addToCart = this.productForm.querySelector('[data-js-product-add-to-cart]');
				this.addToCartText = this.productForm.querySelector('[data-js-product-add-to-cart-text]');
				this.qtyInput = this.productForm.querySelector('input[name="quantity"]');
				this.updateAddToCartText = this.updateAddToCartText.bind(this);
				if ( this.qtyInput ) {
					this.qtyInput.addEventListener('change', this.updateAddToCartText);
				}
			}

			this.addEventListener('change', this.onVariantChange);
			let initVariantChange = false;

			if ( this.hasAttribute('data-variant-required' ) ) {
				this.variantRequired = true;
				this.noVariantSelectedYet = true;
				if ( document.location.search.includes('variant') ) {
					const initVariant = parseInt(new URLSearchParams(document.location.search).get('variant'));
					this.currentVariant = this.getVariantData().find((variant) => {
						if ( variant.id === initVariant ) {
							variant.options.map((option, index) => {
								const variantContainer = this.querySelectorAll(`[data-js-product-variant]`)[index].querySelector(`[data-js-product-variant-container]`);
								if ( variantContainer.dataset.jsProductVariantContainer == 'radio' ) {
									const input = variantContainer.querySelector(`input[value="${CSS.escape(option)}"]`);
									if ( input ) input.checked = true;
								} else if ( variantContainer.dataset.jsProductVariantContainer == 'select' ) {
									variantContainer.value = option;
								}
							})
							return variant;
						}
					});

					if ( this.currentVariant ) {
						this.variantRequired = false;
						this.noVariantSelectedYet = false;
						this.classList.add('variant-selected');
						initVariantChange = true;
					}

				}

			} else {
				this.variantRequired = false;
				this.noVariantSelectedYet = false;
				this.classList.add('variant-selected');
			}

			this.updateOptions();
			this.updateMasterId();
			this.updateUnavailableVariants();

			this.productStock = document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity]`);
			this.productStockProgress = document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-progress]`);
			if ( this.productStock && document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-data]`) ) {
				this.productStockData = JSON.parse(document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-data]`).dataset.inventory);
			}
			this.updateStock();

			this._event = new Event('VARIANT_CHANGE');

			if ( initVariantChange ) {
				this.onVariantChange();
			}

		}

		onVariantChange(){

			this.updateOptions();
			this.updateMasterId();
			this.updateVariantInput();
			if ( this.price ) {
				this.updatePrice();
			}
			this.updateStock();
			this.updateUnavailableVariants();
			this.updatePickupAvailability();
			if ( ! this.hasAttribute('data-no-history') ) {
				this.updateURL();
			}
			this.updateDetails();

			if ( this.productForm ) {
				if ( ! this.currentVariant || ! this.currentVariant.available ) {
					if ( this.productQty ) this.productQty.style.display = 'none';
					this.addToCart.classList.add('disabled');
					this.productForm.classList.add('disabled-cart');
				} else {
					if ( this.productQty ) this.productQty.style.display = '';
					this.addToCart.classList.remove('disabled');
					this.productForm.classList.remove('disabled-cart');
				}
				if ( ! this.currentVariant ) {
					this.productForm.classList.add('unavailable-variant');
				} else {
					this.productForm.classList.remove('unavailable-variant');
				}
				this.updateAddToCartText();
			}
			this.dispatchEvent(this._event);

		}

		updateAddToCartText(){
			if ( ! this.addToCartText ) return;
			if ( ! this.currentVariant ) {
				this.addToCartText.textContent = (this.variantRequired && this.noVariantSelectedYet)
					? OrdoUtils.localeText('products_variant_required')
					: OrdoUtils.localeText('products_unavailable_variant');
				return;
			}
			if ( ! this.currentVariant.available ) {
				this.addToCartText.textContent = OrdoUtils.localeText('products_sold_out_variant');
				return;
			}
			const baseText = this.hasAttribute('data-show-bundle-wording')
				? KROWN.settings.locales.products_add_to_bundle_button
				: this.addToCartText.hasAttribute('data-show-preorder-wording')
				? KROWN.settings.locales.products_preorder_button
				: KROWN.settings.locales.products_add_to_cart_button;
			const qty = parseInt(this.qtyInput?.value, 10) || 1;
			const total = this.currentVariant.price * qty;
			const priceText = this._formatMoney(total, KROWN.settings.shop_money_format);
			// A card's own label (data-add-label, from quick-buy.liquid) is already decoded, so it goes in as text
			const addLabel = this.addToCartText.dataset.addLabel;
			if ( addLabel ) this.addToCartText.textContent = addLabel;
			else this.addToCartText.innerHTML = baseText;
			this.addToCartText.insertAdjacentHTML('beforeend', `<span class="bold-atc-price"> - ${priceText}</span>`);
		}

		updateOptions(){

			this.options = [];

			this.querySelectorAll('[data-js-product-variant-container]').forEach(elm=>{
				if ( elm.dataset.jsProductVariantContainer == 'radio' ) {
					elm.querySelectorAll('.product-variant__input').forEach(el=>{
						if ( el.checked ) {
							this.options.push(el.value);
						}
					});
				} else {
					if ( ! ( this.variantRequired && elm.selectedIndex == 0 ) ) {
						this.options.push(elm.value);
					}
				}
			});

			if ( this.variantRequired && this.noVariantSelectedYet && this.options.length >= parseInt(this.dataset.variants) ) {
				this.noVariantSelectedYet = false;
				this.classList.add('variant-selected');
			}

		}

		updateMasterId(){
			this.currentVariant = this.getVariantData().find((variant) => {
				return !variant.options.map((option, index) => {
					return this.options[index] === option;
				}).includes(false);
			});
		}

		updatePrice(){
			
			if (!this.currentVariant) {
				if ( ! ( this.variantRequired && this.noVariantSelectedYet ) ) {
					this.priceOriginal.innerHTML = '';
					this.priceCompare.innerHTML = '';
					this.priceUnit.innerHTML = '';
				}
			} else {
				this.priceOriginal.innerHTML = this._formatMoney(this.currentVariant.price, KROWN.settings.shop_money_format);
				if ( this.currentVariant.compare_at_price > this.currentVariant.price ) {
					this.priceCompare.innerHTML = this._formatMoney(this.currentVariant.compare_at_price, KROWN.settings.shop_money_format);
				} else {
					this.priceCompare.innerHTML = '';
				}

				if ( this.currentVariant.unit_price_measurement ) {
					this.priceUnit.innerHTML = `
						${this._formatMoney(this.currentVariant.unit_price, KROWN.settings.shop_money_format)} / 
						${( this.currentVariant.unit_price_measurement.reference_value != 1 ? this.currentVariant.unit_price_measurement.reference_value + ' ' : '' )}
						${this.currentVariant.unit_price_measurement.reference_unit}
					`;
				} else {
					this.priceUnit.innerHTML = '';
				}
			}
		
		}

		updateStock(){

			if (!this.currentVariant) {
				if ( this.productStock ) {
					this.productStock.innerHTML = '';
				}
			} else {
				if ( this.productStock && this.productStockData ) {

					let currentVariant = false;
					for ( const variant of this.productStockData ) {
						if ( variant.id == this.currentVariant.id ) {
							currentVariant = variant;
							break;
						}
					}
					this.productStock.innerHTML = '';

					if ( currentVariant ) {

						if ( currentVariant.quantity <= 0 ) {
							if ( currentVariant.inventory == 'continue' ) {
								this.productStock.innerHTML = KROWN.settings.locales.products_preorder;
								this.productStock.setAttribute('data-stock', 'pre-order');
							} else if ( currentVariant.inventory == 'deny' ) {
								this.productStock.innerHTML = KROWN.settings.locales.products_no_products;
								this.productStock.setAttribute('data-stock', 'out-of-stock');
							}
						} else if ( currentVariant.quantity == '1' ) {
							this.productStock.innerHTML = KROWN.settings.locales.products_one_product;
							this.productStock.setAttribute('data-stock', 'one-item-stock');
						} else if ( currentVariant.quantity <= parseInt(this.productStock.dataset.lowStock) ) {
							this.productStock.innerHTML = KROWN.settings.locales.products_few_products.replace('{{ count }}', currentVariant.quantity);
							this.productStock.setAttribute('data-stock', 'little-stock');
						} else if ( currentVariant.unavailable ) {
							this.productStock.innerHTML = KROWN.settings.locales.products_no_products;
							this.productStock.setAttribute('data-stock', 'out-of-stock');
						} else if ( currentVariant.quantity > parseInt(this.productStock.dataset.lowStock) && this.productStock.dataset.type == "always" ) {
							this.productStock.innerHTML = KROWN.settings.locales.products_many_products.replace('{{ count }}', currentVariant.quantity);
							this.productStock.setAttribute('data-stock', 'in-stock');
						} else if ( ! currentVariant.quantity && this.productStock.dataset.type == "always" )  {
							this.productStock.innerHTML = KROWN.settings.locales.products_enough_products;
							this.productStock.setAttribute('data-stock', 'in-stock');
						}

						if ( this.productStockProgress ) {
							let progressQty = 0;
							if ( currentVariant.quantity <= 0 && currentVariant.inventory == 'continue' || typeof currentVariant.quantity === 'undefined' ) {
								progressQty = parseInt(this.productStock.dataset.highStock);
							} else if ( currentVariant.quantity > 0 ) {
								progressQty = currentVariant.quantity;
							}
							if ( progressQty >= parseInt(this.productStock.dataset.highStock)  ) {
								this.productStockProgress.style.width = `100%`;
							} else {
								this.productStockProgress.style.width = `${100 * progressQty / parseInt(this.productStock.dataset.highStock)}%`;
							}
						}

					}
				}
			}
		}

		updateDetails(){

			document.querySelectorAll(`#product-${this.dataset.id} [data-js-product-sku]`).forEach(elm=>{
				if ( this.currentVariant && this.currentVariant.sku ) {
					elm.innerHTML = KROWN.settings.locales.product_sku + this.currentVariant.sku;
				} else {
					elm.innerHTML = '';
				}
			});

			document.querySelectorAll(`#product-${this.dataset.id} [data-js-product-barcode]`).forEach(elm=>{
				if ( this.currentVariant && this.currentVariant.barcode ) {
					elm.innerHTML = KROWN.settings.locales.product_barcode + this.currentVariant.barcode;
				} else {
					elm.innerHTML = '';
				}
			});
			
		}

		updatePickupAvailability() {
			const pickUpAvailability = document.querySelector('pickup-availability');
			if (!pickUpAvailability) return;

			if (this.currentVariant && this.currentVariant.available) {
				pickUpAvailability.fetchAvailability(this.currentVariant.id);
			} else {
				pickUpAvailability.removeAttribute('available');
				pickUpAvailability.innerHTML = '';
			}
		}

		updateUnavailableVariants(){

			// Show ('false') leaves sold-out values unmarked, but combinations with no variant are still marked
			const markSoldOut = this.dataset.hideVariants != 'false';

			if ( markSoldOut || this.dataset.variants != '1' ) {

				if ( this.dataset.variants == '1' ) {

					this.variantData.forEach((variant) => {
						if ( ! variant.available ) {
							const input = this.querySelector(`[data-js-product-variant-container] .product-variant-value[value="${CSS.escape(variant.option1)}"]`);
							if ( ! input ) return;
							if ( this.dataset.hideVariants == 'disable' ) {
								input.setAttribute('disabled', 'disabled');
							} else {
								input.classList.add('disabled');
							}
						}
					});

				} else {

					if ( this.querySelector(':checked') ) {

						const selectedOptionOneVariants = this.variantData.filter(
							(variant) => this.querySelector(':checked').value === variant.option1
						);
	
						const inputWrappers = [...this.querySelectorAll('[data-js-product-variant]')];
						inputWrappers.forEach((option, index) => {
							if (index === 0) return;
							const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
							const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
							const availableOptionInputsValue = selectedOptionOneVariants
								.filter((variant) => (variant.available || ! markSoldOut) && variant[`option${index}`] === previousOptionSelected)
								.map((variantOption) => variantOption[`option${index + 1}`]);
							this.setInputAvailability(optionInputs, availableOptionInputsValue);
						});
	
					}

				}

			}
		}

		setInputAvailability(listOfOptions, listOfAvailableOptions) {
			listOfOptions.forEach((input) => {
				if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
					if ( this.dataset.hideVariants == 'disable' ) {
						input.removeAttribute('disabled');
					} else {
						input.classList.remove('disabled');
					}
				} else {
					if ( this.dataset.hideVariants == 'disable' ) {
						input.setAttribute('disabled', 'disabled');
					} else {
						input.classList.add('disabled');
					}
				}
			});
		}

		updateURL(){
			if (!this.currentVariant) return;
			// Only the variant changes: keep ?view= (preview templates), preview_theme_id, tracking params and the hash.
			const params = new URLSearchParams(window.location.search);
			params.set('variant', this.currentVariant.id);
			window.history.replaceState({ }, '', `${this.dataset.url}?${params}${window.location.hash}`);
		}

		updateVariantInput() {
			if (!this.currentVariant) return;
			// Reuse the form resolved in init() rather than re-querying by id: querySelectorAll
			// matches EVERY node carrying a duplicated id, so on a multi-collection Featured
			// collection section one selection used to write into all of the tabs' forms. The
			// installment form is a genuine page-level singleton, so it stays a global lookup.
			const productForms = [this.productForm, document.getElementById('product-form-installment')];
			productForms.forEach((productForm) => {
				const input = productForm?.querySelector('input[name="id"]');
				if ( ! input ) return;
				input.value = this.currentVariant.id;
				input.dispatchEvent(new Event('change', { bubbles: true }));
			});
		}

		getVariantData() {
			this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
			return this.variantData;
		}

		_formatMoney(cents, format) {
			return OrdoUtils.formatMoney(cents, format);
		}

	}

  if ( typeof customElements.get('product-variants') == 'undefined' ) {
		customElements.define('product-variants', ProductVariants);
	}

}

/* ---
	Product Form
--- */

if ( ! customElements.get('product-form') ) {

	class ProductForm extends HTMLElement {
		constructor() {
			super();   
			this.init();
		}

		init(){
			this.cartType = this.hasAttribute('data-ajax-cart') ? 'ajax' : 'page';
			if ( ! document.body.classList.contains('template-cart') || this.hasAttribute('data-force-form' ) ) {
				this.form = this.querySelector('form');
				this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
				this.ADD_TO_CART = new Event('add-to-cart');
			}
			if ( document.body.classList.contains('touchevents') ) {
				const submitButton = this.querySelector('[type="submit"]');
				if ( submitButton ) {
					submitButton.addEventListener('touchend', e=>{
						//submitButton.click();
					});
				}
			}
		}

		onSubmitHandler(e) {	

			e.preventDefault();
			
			const submitButton = this.querySelector('[type="submit"]');

			// Sold out, unavailable or not chosen yet: the button is only class-disabled, so Enter/Space still reach here
			if ( submitButton?.classList.contains('disabled') ) return;
			// An add is already on its way: a second Enter or key repeat must not add again
			if ( submitButton?.classList.contains('working') ) return;

			submitButton.classList.add('working');
			this._alert?.remove();

			const pageMode = this.cartType === 'page';

			OrdoUtils.cart.request(`${KROWN.settings.routes.cart_add_url}.js`, this._serializeForm(this.form), {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					'X-Requested-With': 'XMLHttpRequest'
				},
				section: ! pageMode
			})
				.then(({ status, message, html }) => {

					if ( ! message && status >= 400 ) {
						throw new Error(`Cart add failed: HTTP ${status}`);
					}

					if ( pageMode ) {
						if ( message ) {
							this._showAlert(message);
						} else {
							document.location.href = KROWN.settings.routes.cart_url;
						}
						return;
					}

					// The item is in the cart, but the drawer can't be redrawn: show the cart page instead.
					if ( ! message && ! html ) {
						document.location.href = KROWN.settings.routes.cart_url;
						return;
					}

					if ( OrdoUtils.cart.render(html) ) {
						if ( message ) OrdoUtils.cart.error(message);
					} else if ( message ) {
						// No cart to show it in: the message stays with the form and the drawer stays shut.
						this._showAlert(message);
						return;
					}

					this.dispatchEvent(this.ADD_TO_CART);

					// a11y
					if ( ! message && document.getElementById('screen-reader-info') ) {
						document.getElementById('screen-reader-info').innerText = OrdoUtils.localeText('cart_announcement');
						setTimeout(()=>{
							document.getElementById('screen-reader-info').innerText = '';
						}, 1000);
					}

				})
				.catch(e => {
					console.error('Cart add error:', e);
					// Network and server failures get an alert too, otherwise the customer sees "working" forever with no feedback.
					this._showAlert(OrdoUtils.localeText('cart_general_error') || 'Sorry, we could not add this to your cart. Please try again.');
				})
				.finally(() => {
					submitButton.classList.remove('working');
				});
		}

		// One alert per form, replaced by the next: below the form in page mode, inside it otherwise.
		_showAlert(message) {
			this._alert?.remove();
			const alert = this._alert = document.createElement('span');
			alert.className = 'alert alert--error';
			alert.textContent = message;
			if ( this.cartType === 'page' ) {
				alert.style.marginTop = '1em';
				alert.style.marginBottom = '0';
				this.form.parentElement.append(alert);
			} else {
				this.form.prepend(alert);
			}
			OrdoUtils.cart.announce(message);
		}

		_serializeForm(form) {
			// Delegates to the shared serializer in assets/utils.js.
			return window.OrdoUtils.serializeForm(form);
		}

	}

  if ( typeof customElements.get('product-form') == 'undefined' ) {
		customElements.define('product-form', ProductForm);
	}

}

/* ---
	Product Recommendations
--- */

if ( ! customElements.get('product-recommendations') ) {
	class ProductRecommendations extends HTMLElement {

		constructor() {

			super();  
      
      const SUCCESS = new Event('product-recommendations-loaded');
      const FAIL = new Event('product-recommendations-error');

      const handleIntersection = (entries, observer) => {

        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);

        fetch(this.dataset.url)
          .then(response => response.text())
          .then(text => {
            const innerHTML = OrdoUtils.parseHTML(text)
                .querySelector('product-recommendations');

								if ( innerHTML && innerHTML.querySelectorAll('[data-js-product-item]').length > 0 ) {
              this.innerHTML = innerHTML.innerHTML;
              this.querySelectorAll('form').forEach(elm=>{
                if (elm.querySelector('template')) {
                  elm.append(elm.querySelector('template').content.cloneNode(true));
                }
              });
              this.dispatchEvent(SUCCESS);
            } else {
							this.dispatchEvent(FAIL);
						}
          })
          .catch(e => {
						this.dispatchEvent(FAIL);
          });
      }

			new IntersectionObserver(handleIntersection.bind(this), {rootMargin: `0px 0px 400px 0px`}).observe(this);

		}

	}
	
  if ( typeof customElements.get('product-recommendations') == 'undefined' ) {
		customElements.define('product-recommendations', ProductRecommendations);
	}

}

/* ---
	Gift card recipent
--- */

if ( ! customElements.get('gift-card-recipient') ) {

	class GiftCardRecipient extends HTMLElement {

		constructor() {

			super();

			const properties = Array.from(this.querySelectorAll('[name*="properties"]'));
			const checkboxPropertyName = 'properties[__shopify_send_gift_card_to_recipient]';

			this.recipientCheckbox = properties.find(input => input.name === checkboxPropertyName);
			this.recipientOtherProperties = properties.filter(input => input.name !== checkboxPropertyName);
			this.recipientFieldsContainer = this.querySelector('.gift-card-recipient__fields');

			if ( this.recipientCheckbox ) {
				this.recipientCheckbox.addEventListener('change', this.synchronizeProperties.bind(this));
			}
			this.synchronizeProperties();

		}

		synchronizeProperties() {
			this.recipientOtherProperties.forEach(property => property.disabled = !this.recipientCheckbox.checked);
			this.recipientFieldsContainer.classList.toggle('hide', !this.recipientCheckbox.checked);
		}

	}

  if ( typeof customElements.get('gift-card-recipient') == 'undefined' ) {
		customElements.define('gift-card-recipient', GiftCardRecipient);
	}

}

/* ---
	Sticky Add to Cart
--- */

if ( ! customElements.get('sticky-add-to-cart') ) {
	class StickyAddToCart extends HTMLElement {

		constructor() {
			super();
			this.init();
		}

		// Re-entrant: <pdp2-section>.refresh() calls it again after an editor re-render replaces the Buy buttons block.
		init() {
			const productPage = document.getElementById(this.dataset.id);
			// Only the Buy buttons block's own button: any other add-to-cart in the column (cross-sell, bundles) adds a different product
			const productButton = productPage ? productPage.querySelector('[data-js-product-add-to-cart][data-main-product-add-to-cart]') : null;
			if ( this._buttonObserver ) {
				this._buttonObserver.disconnect();
				this._buttonObserver = null;
			}
			this._productButton = productButton;

			if ( ! productButton ) {
				// No Buy buttons block in this product section: hide the bar (never .visible, out of the tab order) and warn in the editor.
				this.classList.remove('visible');
				this.hidden = true;
				if ( window.Shopify && window.Shopify.designMode && this.parentNode && ! this.parentNode.querySelector('[data-sticky-atc-warning]') ) {
					const warning = document.createElement('p');
					warning.className = 'pdp-block__warning';
					warning.setAttribute('data-sticky-atc-warning', '');
					warning.textContent = 'Sticky add to cart needs a Buy buttons block.';
					this.parentNode.insertBefore(warning, this);
				}
				return;
			}

			this.hidden = false;
			if ( this.parentNode ) {
				const warning = this.parentNode.querySelector('[data-sticky-atc-warning]');
				if ( warning ) warning.remove();
			}
			if ( ! this._appended ) {
				this._appended = true;
				const target = this.hasAttribute('data-append-to') ? document.querySelector(this.dataset.appendTo) : null;
				( target || document.body ).appendChild(this);
			}
			if ( ! this._bound ) {
				this._bound = true;
				window.addEventListener('scroll', () => this._toggleOnScroll());
				const stickyButton = this.querySelector('button[data-js-atc]');
				if ( stickyButton ) {
					stickyButton.addEventListener('click', () => {
						// A disabled product button won't submit, so no spinner either
						if ( ! this._productButton || this._productButton.classList.contains('disabled') ) return;
						this._productButton.click();
						stickyButton.classList.add('working');
					});
				}
				const chooseButton = this.querySelector('button[data-js-choose]');
				if ( chooseButton ) {
					chooseButton.addEventListener('click', () => {
						const button = this._productButton;
						if ( ! button ) return;
						if ( ! this._isMobile() ) {
							// Desktop: the product details column (data-id) just under the fixed header bar; the button itself as a fallback.
							const details = document.getElementById(this.dataset.id) || button;
							const bar = document.querySelector('.sticky-header');
							const top = details.getBoundingClientRect().top + window.scrollY - ( ( bar ? bar.offsetHeight : 0 ) + 16 );
							window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
							return;
						}
						// Mobile: the buy buttons group at the bottom of the screen. The v2 buy_buttons block and the product-buy-buttons
						// snippet (main-product-alt) wrap it in .product-actions; without one, it scrolls to the button itself.
						const group = button.closest('.product-actions') || button;
						group.scrollIntoView({ behavior: 'smooth', block: 'end' });
					});
				}
			}
			if ( this.hasAttribute('data-single') ) {
				const stickyButton = this.querySelector('button[data-js-atc]');
				this._buttonObserver = new MutationObserver(() => {
					if ( ! stickyButton || ! this._productButton ) return;
					stickyButton.classList.toggle('working', this._productButton.classList.contains('working'));
				});
				this._buttonObserver.observe(productButton, {
					attributes: true, childList: false, subtree: false
				});
			}
			this._toggleOnScroll();
		}

		_isMobile() {
			return window.innerWidth < 768;
		}

		_toggleOnScroll() {
			const productButton = this._productButton;
			if ( ! productButton ) return;
			const rect = productButton.getBoundingClientRect();
			const isInViewport = (
					rect.top >= 0 &&
					rect.left >= 0 &&
					( rect.bottom - 80 ) <= (window.innerHeight || document.documentElement.clientHeight) &&
					rect.right <= (window.innerWidth || document.documentElement.clientWidth)
			);
			const isMobile = this._isMobile();

			if ( ( ! isMobile && rect.top < 0 ) || ( isMobile && ! isInViewport ) ) {
				this.classList.add('visible');
			} else {
				this.classList.remove('visible');
			}
		}

	}

  if ( typeof customElements.get('sticky-add-to-cart') == 'undefined' ) {
		customElements.define('sticky-add-to-cart', StickyAddToCart);
	}

}